import React, { useState, useEffect } from 'react';
import { FixtureData } from '@graphql/generated/graphql';
import Select, { SelectOption } from '@amzn/meridian/select';
import Column from '@amzn/meridian/column';
import Row from '@amzn/meridian/row';
import Text from '@amzn/meridian/text';
import { escapeRegExp } from 'lodash';
import closeMediumTokens from '@amzn/meridian-tokens/base/icon/close-medium';
import Icon from '@amzn/meridian/icon';
import Button from '@amzn/meridian/button';
import Tooltip from '@amzn/meridian/tooltip';
import Tag from '@amzn/meridian/tag';
import Input from '@amzn/meridian/input';
import { ZoneCreationProperties } from '../types/zoneMapType';
import { ProductTaxonomy } from '../types/ProductTaxonomyTypes';
import useFeatureFlag from 'src/hooks/useFeatureFlag';
import FeatureName from '@constants/featureName';
import { stringToColor } from '../zoneViewer/zoneMapColorHelper';

interface ZoneCreationComponentProps {
  zoneIdx: number;
  fixtures: FixtureData[];
  selectedFixtures: string[];
  updateSubcategories: (zoneIdx: number, subcategoryIds?: string[], subcategoryNames?: string[]) => void;
  updateFixtureIds: (zoneIdx: number, fixtureIds?: string[]) => void;
  updateFacings: (zoneIdx: number, facings: number) => void;
  removeZone: (zoneIdx: number) => void;
  zoneCreationProps: ZoneCreationProperties;
  productTaxonomy: ProductTaxonomy;
  isActive?: boolean;
  onActivate?: () => void;
}
const ZoneCreationComponent = (props: ZoneCreationComponentProps) => {
  const {
    zoneIdx,
    fixtures,
    selectedFixtures,
    updateSubcategories,
    updateFixtureIds,
    updateFacings,
    removeZone,
    zoneCreationProps,
    productTaxonomy,
    isActive = false,
    onActivate,
  } = props;

  const nextGenEnabled = useFeatureFlag(FeatureName.NEXT_GEN_V2);

  const zoneName = zoneCreationProps.zoneName || `Zone ${zoneIdx + 1}`;
  // Use the same color logic as the floorplan display
  const zoneColor = stringToColor(zoneName);

  // Set default facings to 8 when nextGenEnabled is false i.e. tool is in v1
  useEffect(() => {
    if (!nextGenEnabled && zoneCreationProps.facings !== 8) {
      updateFacings(zoneIdx, 8);
    }
  }, [nextGenEnabled, zoneCreationProps.facings, zoneIdx, updateFacings]);

  function onCloseSubcategory(subcategoryId: string, subcategoryName: string) {
    const subcategoryIds = zoneCreationProps.subcategoryIds.filter((id) => id !== subcategoryId);
    const subcategoryNames = zoneCreationProps.subcategoryNames.filter((name) => name !== subcategoryName);
    updateSubcategories(zoneIdx, subcategoryIds, subcategoryNames);
  }

  function onCloseFixture(fixtureId: string) {
    const fixtureIds = zoneCreationProps.fixtureIds.filter((id) => id !== fixtureId);
    updateFixtureIds(zoneIdx, fixtureIds);
  }

  const [subcategoryFilter, setSubcategoryFilter] = useState();
  const searchSubCategoryRegExp = new RegExp(escapeRegExp(subcategoryFilter), 'i');
  const filteredSubcategoryOptions = productTaxonomy?.subcategoryList?.filter(
    (option) =>
      (!zoneCreationProps.categoryId || option.categoryId === zoneCreationProps.categoryId) &&
      (!subcategoryFilter || searchSubCategoryRegExp.test(option.subcategoryName)),
  );

  const subcategoryIdToSubcategoryNameMap: Map<string, string> = new Map();
  productTaxonomy?.subcategoryList?.forEach((subcategory) => {
    subcategoryIdToSubcategoryNameMap.set(subcategory.subcategoryId, subcategory.subcategoryName);
  });

  const [fixtureFilter, setFixtureFilter] = useState();
  const searchFixtureRegExp = new RegExp(escapeRegExp(fixtureFilter), 'i');
  const filteredFixtureOptions = fixtures.filter(
    (option) => !fixtureFilter || searchFixtureRegExp.test(option.id ?? ''),
  );
  const availableFixtures = filteredFixtureOptions.filter(
    (option) => zoneCreationProps.fixtureIds.includes(option.id) || !selectedFixtures.includes(option.id),
  );
  const subcategoryOnChange = (subcategoryIds: string[]): void => {
    const subcategoryNames = subcategoryIds.map(
      (subcategoryId) => subcategoryIdToSubcategoryNameMap.get(subcategoryId)!,
    );
    updateSubcategories(zoneIdx, subcategoryIds, subcategoryNames);
  };

  return (
    <div
      onClick={() => onActivate && onActivate()}
      style={{
        cursor: onActivate ? 'pointer' : 'default',
        padding: '12px',
        border: isActive ? `3px solid ${zoneColor}` : '1px solid #e0e0e0',
        borderRadius: '8px',
        backgroundColor: isActive ? `${zoneColor}15` : 'transparent',
        transition: 'all 0.2s ease',
        boxSizing: 'border-box',
        width: '100%',
        maxWidth: '335px',
      }}
      role={onActivate ? 'button' : undefined}
      tabIndex={onActivate ? 0 : undefined}
      aria-label={onActivate ? `Set ${zoneName} as active zone` : undefined}
    >
      <Column spacing="300">
        {/* Zone header with color indicator and name */}
        <Row alignmentVertical="center" alignmentHorizontal="justify">
          <Row spacing="200" alignmentVertical="center">
            <div
              style={{
                width: '16px',
                height: '16px',
                backgroundColor: zoneColor,
                borderRadius: '3px',
                border: '1px solid #333',
              }}
              aria-hidden="true"
            />
            <Text type="h300">{zoneName}</Text>
            {isActive && <span style={{ color: zoneColor, fontWeight: 'bold', fontSize: '12px' }}>(Active)</span>}
          </Row>
          <div onClick={(e) => e.stopPropagation()} style={{ pointerEvents: 'auto' }}>
            <Tooltip position="end" title="Remove the zone below">
              <Button
                onClick={() => {
                  removeZone(zoneIdx);
                }}
                size="small"
              >
                <Icon tokens={closeMediumTokens} />
              </Button>
            </Tooltip>
          </div>
        </Row>
        <Select
          label="Product type"
          value={zoneCreationProps.subcategoryIds}
          onChange={subcategoryOnChange}
          searchQuery={subcategoryFilter}
          onSearch={setSubcategoryFilter}
          placeholder="Choose product type(s)..."
          width="100%"
        >
          {filteredSubcategoryOptions?.map((option) => (
            <SelectOption label={option.subcategoryName} value={option.subcategoryId} key={option.subcategoryId} />
          ))}
          {!filteredSubcategoryOptions || filteredSubcategoryOptions.length === 0 ? (
            <Column alignmentVertical="center" spacing="300" spacingInset="600">
              <Text alignment="center">No results</Text>
            </Column>
          ) : null}
        </Select>
        <Row spacing="200" wrap="down" width="100%">
          {zoneCreationProps.subcategoryIds?.map((subcategoryId) => (
            <Tag
              key={subcategoryId}
              onClose={() => {
                onCloseSubcategory(subcategoryId, subcategoryIdToSubcategoryNameMap.get(subcategoryId)!);
              }}
              type="neutral"
            >
              {subcategoryIdToSubcategoryNameMap.get(subcategoryId)!}
            </Tag>
          ))}
        </Row>
        <Select
          label="Fixtures"
          value={zoneCreationProps.fixtureIds}
          onChange={(fixtureIds: string[]) => updateFixtureIds(zoneIdx, fixtureIds)}
          searchQuery={fixtureFilter}
          onSearch={setFixtureFilter}
          placeholder="Choose fixtures..."
          width="100%"
        >
          {availableFixtures.map((option) =>
            option.id ? <SelectOption label={option.id} value={option.id} key={option.id} /> : null,
          )}
          {!filteredFixtureOptions || filteredFixtureOptions.length === 0 ? (
            <Column alignmentVertical="center" spacing="300" spacingInset="600">
              <Text alignment="center">No results</Text>
            </Column>
          ) : null}
        </Select>
        <Row spacing="200" wrap="down" width="100%">
          {zoneCreationProps.fixtureIds?.map((fixtureId) => (
            <Tag
              key={fixtureId}
              onClose={() => {
                onCloseFixture(fixtureId);
              }}
              type="neutral"
            >
              {fixtureId}
            </Tag>
          ))}
        </Row>
        {nextGenEnabled && (
          <Input
            label="Facings per fixture"
            type="number"
            value={zoneCreationProps.facings?.toString() || ''}
            onChange={(value: string) => updateFacings(zoneIdx, parseInt(value) || 0)}
            placeholder="Enter number of facings..."
            width="100%"
          />
        )}
      </Column>
    </div>
  );
};

export default ZoneCreationComponent;
